import React from "react";

import WComponent from "../common/WComponent";
import ToolsReviewSection from "../clients/Tools/kit/ToolsReviewSection";
import ToolsReviewItem, { ToolsReviewResolution } from "../clients/Tools/kit/ToolsReviewItem";
import PlaybookReviewService from "../../services/PlaybookReviewService";
import UtilityService from "../../services/UtilityService";
import AgentFindingSteps from "./AgentFindingSteps";
import AgentReviewItemTools from "./AgentReviewItemTools";
import AgentFindingProof from "./AgentFindingProof";

import "./agent-run-report.css";

class AgentRunFindings extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			openEvidenceKeys: [],
			openLastMonthKeys: [],
		};
	}

	onToggleKey = ({ listName, key }) => {
		const nextList = UtilityService.toggleInList({ list: this.state[listName], item: key });
		this.update({ [listName]: nextList });
	};

	renderLastMonthNote({ lastMonth }) {
		const { decisionLabel, decidedBy, reasonText, amountNote } = PlaybookReviewService.describeLastMonthDecision({ lastMonth });
		return (
			<div className="agent-run-report__aside">
				<span className="agent-run-report__aside-label">Last month</span>
				{decisionLabel} by {decidedBy}
				{reasonText ? ` — ${reasonText}` : ""} · {amountNote}
			</div>
		);
	}

	renderClosingSteps({ finding, findingKey }) {
		const { review, busySubjectKey, onDecide, onConfirmStillApplies } = this.props;
		return (
			<AgentFindingSteps
				subject={finding}
				isProposal={false}
				review={review}
				isBusy={busySubjectKey === findingKey}
				onDecide={choice => onDecide({ findingId: finding.id, ...choice })}
				onConfirmStillApplies={() => onConfirmStillApplies({ findingId: finding.id })}
			/>
		);
	}

	renderScriptSource({ finding }) {
		const scriptSourceText = PlaybookReviewService.describeScriptSource({ scriptSource: finding.scriptSource });
		if (!scriptSourceText) return null;
		return <p className="agent-run-report__finding-flag agent-run-report__finding-flag--source">{scriptSourceText}</p>;
	}

	renderEvidence({ finding }) {
		const { onOpenBooksPanel } = this.props;
		const evidenceSteps = finding.decisionRecord ? finding.decisionRecord.evidenceSteps || [] : [];
		return (
			<div className="agent-run-report__aside">
				<span className="agent-run-report__aside-label">Evidence</span>
				{finding.evidence}
				{evidenceSteps.length > 0 && (
					<button
						type="button"
						className="tk-text-action"
						onClick={() => onOpenBooksPanel({ findingId: finding.id, qboAccountId: finding.qboAccountId || null, tab: "frozen", title: finding.issue })}
					>
						Show the run {evidenceSteps.length === 1 ? "step" : "steps"} it came from ({evidenceSteps.join(", ")})
					</button>
				)}
			</div>
		);
	}

	renderFinding = finding => {
		const { openBooksFindingId, onOpenBooksPanel } = this.props;
		const { openEvidenceKeys, openLastMonthKeys } = this.state;
		const qboAccountId = finding.qboAccountId || null;
		const isBooksPanelOpen = openBooksFindingId === finding.id;
		const findingKey = `finding-${finding.id}`;
		const hasAmount = Number(finding.amount) > 0;
		const isAddedByCheck = finding.source === "check" && !finding.reportedByAgent;
		const detail = [finding.account, hasAmount ? `$${UtilityService.formatCurrency(finding.amount)}` : null].filter(Boolean).join(" · ");

		return (
			<ToolsReviewItem key={findingKey} id={findingKey} status={PlaybookReviewService.reviewLookFor({ finding })} label={finding.issue} detail={detail || null}>
				{finding.explanation && <p className="agent-run-report__finding-explanation">{finding.explanation}</p>}
				<AgentFindingProof
					proof={finding.proof}
					cardAccount={{ qboAccountId, name: finding.account || null }}
					onSeeRows={() => onOpenBooksPanel({ findingId: finding.id, qboAccountId, tab: "frozen", title: finding.issue })}
				/>
				{isAddedByCheck && <p className="agent-run-report__finding-flag">The checks found this; the agent did not explain it.</p>}
				{finding.checkNote && <p className="agent-run-report__finding-flag">{finding.checkNote}</p>}
				{this.renderScriptSource({ finding })}
				{finding.accountNeedsLinking && (
					<p className="agent-run-report__finding-flag">
						{finding.account} is not linked to the client's chart yet, so this finding is not on a line. Link it in Bookkeeping's QuickBooks trial balance.
					</p>
				)}
				{finding.nextStep && (
					<ToolsReviewResolution text={finding.nextStep} kindLabel={PlaybookReviewService.resolutionKindFor({ finding })}>
						{this.renderClosingSteps({ finding, findingKey })}
					</ToolsReviewResolution>
				)}
				{!finding.nextStep && this.renderClosingSteps({ finding, findingKey })}
				{openEvidenceKeys.includes(findingKey) && this.renderEvidence({ finding })}
				{finding.lastMonth && openLastMonthKeys.includes(findingKey) && this.renderLastMonthNote({ lastMonth: finding.lastMonth })}
				<AgentReviewItemTools
					isEvidenceOpen={openEvidenceKeys.includes(findingKey)}
					onToggleEvidence={() => this.onToggleKey({ listName: "openEvidenceKeys", key: findingKey })}
					hasLastMonth={!!finding.lastMonth && !!finding.decision}
					isLastMonthOpen={openLastMonthKeys.includes(findingKey)}
					onToggleLastMonth={() => this.onToggleKey({ listName: "openLastMonthKeys", key: findingKey })}
					isShowMeOpen={isBooksPanelOpen}
					onShowMe={qboAccountId ? () => onOpenBooksPanel({ findingId: finding.id, qboAccountId, tab: "ledger", title: finding.issue }) : null}
				/>
			</ToolsReviewItem>
		);
	};

	renderFindingSection = section => {
		const { collapsedAreas, onToggleArea } = this.props;
		const isExpanded = !(collapsedAreas || {})[section.area];
		const severityCounts = PlaybookReviewService.groupFindingsBySeverity({ findings: section.findings }).map(
			group => `${group.findings.length} ${group.label.toLowerCase()}`
		);

		return (
			<ToolsReviewSection
				key={section.area}
				title={section.area}
				meta={severityCounts.join(" · ")}
				doneCount={section.decidedCount}
				totalCount={section.findings.length}
				isExpanded={isExpanded}
				onToggle={() => onToggleArea({ area: section.area, isExpanded })}
			>
				{section.findings.map(this.renderFinding)}
			</ToolsReviewSection>
		);
	};

	render() {
		const { review } = this.props;
		const findings = review ? review.findings : [];
		const sections = PlaybookReviewService.groupFindingsByArea({ findings });
		if (sections.length === 0) return null;
		return <div className="afs-review agent-run-report__checklist">{sections.map(this.renderFindingSection)}</div>;
	}
}

export default AgentRunFindings;
